<template>
  <Viewer2D
    ref="baseViewer"
    @ready="setViewerRef"
  />
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import OpenSeadragon from 'openseadragon';
import OpenSeadragonSelection from 'openseadragonselection/dist/openseadragonselection.js';
OpenSeadragon.selection = OpenSeadragonSelection;

import Viewer2D from '@/components/common/viewer2D/Viewer2D';

export default {
  name: 'Viewer2DObservations',

  components: {
    Viewer2D
  },

  props: {
    modeObservation: {
      type: Boolean,
      default: () => false
    },

    currentImage: {
      type: Object,
      default: () => ({})
    },

    isCurrentImageLoaded: {
      type: Boolean,
      default: () => false
    }
  },

  setup() {
    let unsubscribeActions = null;

    return { unsubscribeActions };
  },

  data() {
    return {
      selectionViewer: null,
      isViewerLoaded: false
    };
  },

  computed: {
    ...mapState('observations', ['loadedObs', 'currentObs', 'obsIsGeneral']),
    ...mapGetters('observations', ['loadedObsToDisplay', 'editingObs', 'addingObs'])
  },

  watch: {

    isViewerLoaded() {
      if(this.isCurrentImageLoaded) {
        this.loadImage();
        this.initObservationsLogic();
      }
    },

    isCurrentImageLoaded() {
      if(this.isViewerLoaded) {
        this.loadImage();
        this.initObservationsLogic();
      }
    },

    currentImage() {
      this.loadImage();
      this.initObservationsLogic();
    },

    modeObservation() {
      if(this.isViewerLoaded && this.isCurrentImageLoaded) {
        this.initObservationsLogic();
      }
    },

    obsIsGeneral() {
      //If user creates a general observation doesn't allow to draw rectangles
      this.obsIsGeneral? this.disableSelection() : this.enableSelection();
    }
  },

  mounted() {
    this.unsubscribeActions = this.$store.subscribeAction({
      before: (action) => {
        if (action.type === 'user/login' || action.type === 'user/logout') {
          // Reload observations if user logged in after activating mode observations
          this.reset();
        }
      },
      after: (action) => {
        if (action.type === 'observations/editObs') {
          this.unDrawFixedRectangle(action.payload);
          this.drawSelection(this.currentObs);
        }
        if (action.type === 'observations/reloadObs' || action.type === 'observations/submitOne' || action.type === 'observations/deleteObs') {
          this.clearViewerOverlays();
          this.resetSelection();
          /* Redraw like it was before update */
          this.drawObservations(this.loadedObsToDisplay);
        }
        if (action.type === 'user/login' || action.type === 'user/logout') {
          // Reload observations if user logged in after activating mode observations
          this.initObservationsLogic();
        }
      }
    });
  },

  beforeUnmount() {
    if(this.unsubscribeActions) this.unsubscribeActions();
  },

  methods: {
    setViewerRef() {
      this.viewer2D = this.$refs.baseViewer.viewer2D;
      this.isViewerLoaded = true;
    },

    loadImage(){
      this.$refs.baseViewer.openImage(this.currentImage.media.tiles.url,this.currentImage.media.regionByPx);
    },

    initObservationsLogic() {
      this.resetAllObsValues();
      this.clearViewerOverlays();
      this.resetSelection();

      if (!this.modeObservation) {
        this.disableSelection();
        return;
      }

      if (!this.editingObs) {
        //if not editing existing observation, create a new one
        this.createObs();
      }

      // Load observations
      this.loadObs({ imageId: this.$route.params.imageId }).then(() => {
        if(!this.selectionViewer) {
          this.initSelection();
        }

        // Add observations
        this.drawObservations(this.loadedObsToDisplay);

        // Draw Selection if an observation is still open
        if (this.editingObs || this.addingObs) {
          this.drawSelection(this.currentObs);
        }
      });
    },

    disableSelection() {
      if (this.selectionViewer) {
        this.selectionViewer.disable();
      }
    },

    enableSelection() {
      if (this.selectionViewer) {
        this.selectionViewer.enable();
      }
    },

    resetSelection() {
      /* Reset selection by toogling */
      this.disableSelection();
      this.enableSelection();
    },

    clearViewerOverlays() {
      if (this.selectionViewer) {
        this.selectionViewer.viewer.clearOverlays();
      }
    },

    drawObservations(obsArray) {
      for (const [index, obs] of obsArray.entries()) {
        if (obs.coord_x && obs.coord_y && obs.width && obs.height) { //if general observation, does not draw a rectangle
          this.unDrawFixedRectangle(index);
          this.drawFixedRectangle(index, obs);
        }
      }
    },

    undrawObservations(obsArray) {
      for (let index = 0; index < obsArray.length; index++) {
        this.unDrawFixedRectangle(index);
      }
    },

    drawSelection(obs) {
      if (!obs.coord_x || !obs.coord_y || !obs.width || !obs.height) {
        // nothing to draw
        return;
      }
      const rect = new OpenSeadragon.SelectionRect(
        parseFloat(obs.coord_x),
        parseFloat(obs.coord_y),
        parseFloat(obs.width),
        parseFloat(obs.height)
      );
      this.selectionViewer.rect = rect;
      this.selectionViewer.draw();
    },

    drawFixedRectangle(index, obs) {
      const rect = new OpenSeadragon.Rect(
        parseFloat(obs.coord_x),
        parseFloat(obs.coord_y),
        parseFloat(obs.width),
        parseFloat(obs.height)
      );
      // Draw rectangle and add Overlay
      const div = document.createElement('div');
      div.classList.add('rect-observation');
      div.id = 'obsSelection-' + index;
      this.viewer2D.viewport.viewer.addOverlay(div, rect);
    },

    unDrawFixedRectangle(index) {
      const idHTML = 'obsSelection-' + index;
      if (this.viewer2D.viewport.viewer.getOverlayById(idHTML)) {
        this.viewer2D.viewport.viewer.removeOverlay(idHTML);
      }
    },

    initSelection() {
      this.selectionViewer = this.viewer2D.selection({ // viewport.viewer
        element: null, // html element to use for overlay
        showSelectionControl: false, // show button to toggle selection mode
        toggleButton: null,
        showConfirmDenyButtons: false,
        returnPixelCoordinates: false, // We want relative coordinates
        keyboardShortcut: null, // 'c', // key to toggle selection mode
        allowRotation: false, // turn selection rotation on or off as needed
        restrictToImage: true, // true = do not allow any part of the selection to be outside the image
        onMoveStart: () => {
        },
        onMoveEnd: (rect) => {
          this.setDimensions(rect);
        },
        borderStyle: { // overwriteable style defaults
          width: '4px',
          color: getComputedStyle(document.documentElement).getPropertyValue('--color-orange-300').trim()
        },
        cornersStyle: {
          width: '12px',
          height: '12px',
          background: getComputedStyle(document.documentElement).getPropertyValue('--color-orange-300').trim(),
          border: '0'
        }
      });
      this.selectionViewer.enable();
    },

    ...mapActions('observations', ['loadObs', 'createObs', 'setDimensions', 'resetAllObsValues']),
    ...mapActions('app', ['toggleModeObservation'])
  }
};
</script>

<style lang="postcss">
/* purgecss start ignore */
.selection-box {
  & .border-0-handle, & .border-1-handle, & .border-2-handle, & .border-3-handle{
    display:none;
  }
  & .corner-0-handle,  & .corner-1-handle, & .corner-2-handle , & .corner-3-handle{
    border-radius:50%;
  }
  cursor: move;
  & .corner-0-handle{
    cursor: nw-resize;
  }
  & .corner-1-handle{
    cursor: ne-resize;
  }
  & .corner-2-handle{
    cursor: se-resize;
  }
  & .corner-3-handle{
    cursor: sw-resize;
  }
  & .border-0-handle, & .border-0{
    cursor: n-resize;
  }
  & .border-1-handle, & .border-1{
    cursor: e-resize;
  }
  & .border-2-handle, & .border-2{
    cursor: s-resize;
  }
  & .border-3-handle, & .border-3{
    cursor: w-resize;
  }
}

/* Fixed rectangle */
.rect-observation{
  @apply bg-gray-300 bg-opacity-30 border-4 border-teal-300;
}

.rect-observation--selected{
  @apply bg-orange-300 bg-opacity-30 border-orange-300;
}
/* purgecss end ignore */
</style>
